import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { ChipMultiSelect } from '@components/ui/forms/ChipMultiSelect';
import { getRoleDisplayName, ROLES } from '@utils/roleUtils';

/**
 * One editable row in the demo-org roster. `rowId` is a stable client-only key for React /
 * cross-row references and is never sent to the backend; persona `key` is the server-facing
 * slug (and the provider_mapping key).
 */
export interface PersonaDraft {
  rowId: string;
  key: string;
  first_name: string;
  last_name: string;
  roles: string[];
  is_owner: boolean;
  needs_cosign: boolean;
  supervisor_key: string; // '' = none
}

const ROLE_OPTIONS = Object.values(ROLES).map((role) => ({
  value: role,
  label: getRoleDisplayName(role),
}));

export function makeBlankPersona(): PersonaDraft {
  return {
    rowId: crypto.randomUUID(),
    key: '',
    first_name: '',
    last_name: '',
    roles: [ROLES.CLINICIAN],
    is_owner: false,
    needs_cosign: false,
    supervisor_key: '',
  };
}

interface DemoOrgRosterEditorProps {
  personas: PersonaDraft[];
  onChange: (next: PersonaDraft[]) => void;
  disabled?: boolean;
}

export function DemoOrgRosterEditor({
  personas,
  onChange,
  disabled = false,
}: DemoOrgRosterEditorProps) {
  const updateRow = (rowId: string, patch: Partial<PersonaDraft>) => {
    onChange(personas.map((p) => (p.rowId === rowId ? { ...p, ...patch } : p)));
  };

  const setOwner = (rowId: string) => {
    onChange(personas.map((p) => ({ ...p, is_owner: p.rowId === rowId })));
  };

  const addRow = () => {
    onChange([...personas, makeBlankPersona()]);
  };

  const removeRow = (rowId: string) => {
    const removed = personas.find((p) => p.rowId === rowId);
    const next = personas
      .filter((p) => p.rowId !== rowId)
      // Drop dangling supervisor references to the removed persona's key.
      .map((p) =>
        removed && p.supervisor_key === removed.key
          ? { ...p, supervisor_key: '' }
          : p,
      );
    onChange(next);
  };

  return (
    <Stack spacing={2}>
      {personas.map((persona, index) => {
        const supervisorOptions = personas.filter(
          (p) => p.rowId !== persona.rowId && p.key.trim() !== '',
        );
        return (
          <Box
            key={persona.rowId}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
            }}
          >
            <Stack
              direction="row"
              sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Persona {index + 1}
              </Typography>
              <Tooltip title="Remove persona">
                <span>
                  <IconButton
                    aria-label={`Remove persona ${persona.key || index + 1}`}
                    size="small"
                    onClick={() => removeRow(persona.rowId)}
                    disabled={disabled || personas.length <= 1}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 2,
              }}
            >
              <TextField
                label="Key"
                value={persona.key}
                onChange={(e) => updateRow(persona.rowId, { key: e.target.value })}
                disabled={disabled}
                size="small"
                helperText="Stable slug (provider_mapping key)"
              />
              <TextField
                label="First name"
                value={persona.first_name}
                onChange={(e) =>
                  updateRow(persona.rowId, { first_name: e.target.value })
                }
                disabled={disabled}
                size="small"
              />
              <TextField
                label="Last name"
                value={persona.last_name}
                onChange={(e) =>
                  updateRow(persona.rowId, { last_name: e.target.value })
                }
                disabled={disabled}
                size="small"
              />
            </Box>

            <Box sx={{ mt: 2 }}>
              <ChipMultiSelect
                labelId={`roles-${persona.rowId}`}
                label="Roles"
                value={persona.roles}
                options={ROLE_OPTIONS}
                onChange={(roles) => updateRow(persona.rowId, { roles })}
                onOnly={(role) => updateRow(persona.rowId, { roles: [role] })}
                onClear={() => updateRow(persona.rowId, { roles: [] })}
                disabled={disabled}
                minWidth={260}
              />
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ alignItems: { sm: 'center' }, mt: 2 }}
            >
              <Tooltip title="The persona whose signup creates and anchors the org — every other persona joins it. Exactly one required.">
                <FormControlLabel
                  control={
                    <Radio
                      checked={persona.is_owner}
                      onChange={() => setOwner(persona.rowId)}
                      disabled={disabled}
                      size="small"
                    />
                  }
                  label="Owner"
                />
              </Tooltip>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={persona.needs_cosign}
                    onChange={(e) =>
                      updateRow(persona.rowId, { needs_cosign: e.target.checked })
                    }
                    disabled={disabled}
                    size="small"
                  />
                }
                label="Needs co-sign"
              />
              <Select
                value={persona.supervisor_key}
                onChange={(e) =>
                  updateRow(persona.rowId, { supervisor_key: e.target.value })
                }
                disabled={disabled || !persona.needs_cosign}
                size="small"
                displayEmpty
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">
                  <em>Any supervisor</em>
                </MenuItem>
                {supervisorOptions.map((p) => (
                  <MenuItem key={p.rowId} value={p.key}>
                    {p.key}
                    {p.first_name || p.last_name
                      ? ` (${p.first_name} ${p.last_name})`.trimEnd()
                      : ''}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </Box>
        );
      })}

      <Box>
        <Button
          startIcon={<AddIcon />}
          onClick={addRow}
          disabled={disabled}
          variant="outlined"
          size="small"
        >
          Add persona
        </Button>
      </Box>
    </Stack>
  );
}
